import { jsPDF } from 'jspdf';
import { isEmbeddedFontLoaded, registerEmbeddedFont } from '../pdf/embeddedFont';

/**
 * Aushang zum Kundenlink einer Unterweisung (A4 hoch).
 *
 * Der Link zur Unterweisung wird nicht nur per E-Mail verteilt: auf der
 * Baustelle, im Pausenraum und am Schwarzen Brett haengt ein Blatt mit einer
 * Ueberschrift, zwei Saetzen und einem grossen QR-Code. Dieses Blatt entsteht
 * hier — reine Funktion wie zertifikatPdf.ts: Eingaben rein, Bytes raus,
 * kein `window`, kein `document`, kein `doc.save()`.
 *
 * Der QR-Code kommt fertig als DataURL vom Aufrufer (`qrcode`.toDataURL), die
 * Bibliothek laeuft also nur dort, wo der Knopf gedrueckt wird. Der Link steht
 * zusaetzlich als Text unter dem Code — wer nicht scannen kann, tippt ab.
 *
 * Schrift wie beim Zertifikat: eingebettete Liberation Sans, wenn der
 * Aufrufer sie vorher mit `ensureEmbeddedFont()` geladen hat; sonst
 * Helvetica (Umlaute lesbar, nur nicht PDF/A).
 */
export interface AushangDaten {
    /** Grosse Ueberschrift, vom Innendienst frei aenderbar (Vorgabe: Titel der Unterweisung). */
    ueberschrift: string;
    /** Kurzer Text unter der Ueberschrift, Absaetze durch Leerzeile getrennt. */
    text: string;
    /** Der Kundenlink, wie er im QR-Code steckt. */
    url: string;
    /** QR-Code als DataURL (PNG). */
    qrDataUrl: string;
    /** Betreuendes Buero fuer die Fusszeile, z. B. „GefAS mbH". */
    bueroName: string;
    /** Kunde = Projekt; erscheint klein ueber der Ueberschrift, darf leer bleiben. */
    kundeName?: string | null;
    /** Nummer und Titel der Unterweisung fuer die Fusszeile („Unterweisung Nr. 3 — Titel"). */
    unterweisungNummer?: number | null;
    unterweisungTitel?: string | null;
    /**
     * Was das Blatt bewirbt — Vorgabe „Unterweisung". Die Einweisung in den
     * SiGe-Plan nutzt dasselbe Blatt mit „Einweisung": Fusszeile
     * („Einweisung Nr. 3") und Ersatz-Ueberschrift, falls keine gesetzt ist.
     */
    artLabel?: string;
}

const SEITE_B = 210;
const SEITE_H = 297;
const RAND = 20;
const BREITE = SEITE_B - 2 * RAND;

/** Absaetze: Leerzeile trennt, einfache Umbrueche werden zu Leerzeichen. */
export const absaetze = (text: string | null | undefined): string[] =>
    (text || '')
        .replace(/\r\n?/g, '\n')
        .split(/\n\s*\n/)
        .map((a) => a.replace(/\s*\n\s*/g, ' ').trim())
        .filter(Boolean);

export const erzeugeAushangPdf = (daten: AushangDaten): ArrayBuffer => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    if (isEmbeddedFontLoaded()) registerEmbeddedFont(doc);

    const s = (v: string | null | undefined) => (v ?? '').toString().trim();
    const artLabel = s(daten.artLabel) || 'Unterweisung';
    let y = RAND + 8;

    // ── Kunde, klein ueber der Ueberschrift ────────────────────────────────
    if (s(daten.kundeName)) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(100, 116, 139);
        doc.text(s(daten.kundeName), SEITE_B / 2, y, { align: 'center' });
        y += 10;
    } else {
        y += 4;
    }

    // ── Ueberschrift: gross, umbrochen, mittig ─────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42);
    let groesse = 30;
    let zeilen: string[] = [];
    // Schrift verkleinern, bis die Ueberschrift in hoechstens drei Zeilen passt —
    // ein sechszeiliger Titel wuerde den QR-Code nach unten vom Blatt schieben.
    for (; groesse >= 18; groesse -= 2) {
        doc.setFontSize(groesse);
        zeilen = doc.splitTextToSize(s(daten.ueberschrift) || artLabel, BREITE) as string[];
        if (zeilen.length <= 3) break;
    }
    doc.text(zeilen, SEITE_B / 2, y, { align: 'center' });
    y += zeilen.length * (groesse * 0.42) + 8;

    // ── Kurztext ──────────────────────────────────────────────────────────
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(13);
    doc.setTextColor(51, 65, 85);
    for (const absatz of absaetze(daten.text)) {
        const az = doc.splitTextToSize(absatz, BREITE - 10) as string[];
        doc.text(az, SEITE_B / 2, y, { align: 'center' });
        y += az.length * 6.2 + 4;
    }
    y += 6;

    // ── QR-Code: so gross, wie der Rest der Seite erlaubt (min. 80, max. 120 mm) ──
    const fussY = SEITE_H - RAND - 14;
    const platz = fussY - y - 24; // Platz fuer Link-Zeile darunter
    const qrB = Math.max(80, Math.min(120, platz));
    const qrX = (SEITE_B - qrB) / 2;
    try {
        doc.addImage(daten.qrDataUrl, 'PNG', qrX, y, qrB, qrB);
    } catch {
        // Ein unlesbares Bild laesst das Blatt trotzdem entstehen — der Link
        // steht darunter als Text.
        doc.setDrawColor(203, 213, 225);
        doc.rect(qrX, y, qrB, qrB);
    }
    y += qrB + 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(13, 148, 136);
    doc.text('Mit dem Smartphone scannen', SEITE_B / 2, y, { align: 'center' });
    y += 6;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    const urlZeilen = doc.splitTextToSize(s(daten.url), BREITE) as string[];
    doc.text(urlZeilen, SEITE_B / 2, y, { align: 'center' });

    // ── Fusszeile ─────────────────────────────────────────────────────────
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(RAND, fussY, SEITE_B - RAND, fussY);
    doc.setFontSize(8.5);
    doc.setTextColor(148, 163, 184);
    const links = [
        daten.unterweisungNummer ? `${artLabel} Nr. ${daten.unterweisungNummer}` : '',
        s(daten.unterweisungTitel),
    ].filter(Boolean).join(' — ');
    if (links) doc.text(doc.splitTextToSize(links, BREITE * 0.6)[0] as string, RAND, fussY + 5);
    doc.text(s(daten.bueroName), SEITE_B - RAND, fussY + 5, { align: 'right' });

    return doc.output('arraybuffer');
};
